import React, { useState, useEffect } from 'react';

interface CircularProgressSpinnerProps {
  percentage: number;
  totalFiles?: number;
}

const CircularProgressSpinner: React.FC<CircularProgressSpinnerProps> = ({ percentage, totalFiles = 1 }) => {
  const [displayPercentage, setDisplayPercentage] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    if (percentage === 0 && !hasStarted) {
      // Stage 1: Show 0% for 2 seconds
      setDisplayPercentage(0);
      
      const timer1 = setTimeout(() => {
        // Stage 2: Show half of the expected increment (25% for 2 files)
        const incrementPerFile = 100 / totalFiles;
        const halfIncrement = incrementPerFile / 2;
        setDisplayPercentage(Math.round(halfIncrement));
        setHasStarted(true);
      }, 2000);

      return () => clearTimeout(timer1);
    } else if (percentage > 0) {
      // Stage 3: Show actual progress
      setDisplayPercentage(percentage);
      setHasStarted(true);
    }
  }, [percentage, totalFiles, hasStarted]);

  // Calculate the stroke-dasharray for the progress circle
  const radius = 100; // Increased radius for bigger circle
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (displayPercentage / 100) * circumference;

  return (
    <div className="relative w-64 h-64 flex items-center justify-center">
      <svg
        className="w-64 h-64 transform -rotate-90"
        width="256"
        height="256"
        viewBox="0 0 256 256"
        aria-label={`Upload progress: ${percentage}%`}
        role="progressbar"
        aria-valuenow={percentage}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        {/* Define gradient using theme colors */}
        <defs>
          <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="hsl(var(--primary))" />
            <stop offset="25%" stopColor="hsl(var(--primary) / 0.9)" />
            <stop offset="50%" stopColor="hsl(var(--primary) / 0.8)" />
            <stop offset="75%" stopColor="hsl(var(--primary) / 0.7)" />
            <stop offset="100%" stopColor="hsl(var(--primary) / 0.6)" />
          </linearGradient>
          
          {/* Glowing effect */}
          <filter id="glow">
            <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
            <feMerge> 
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Background circle */}
        <circle
          cx="128"
          cy="128"
          r={radius}
          stroke="hsl(var(--muted-foreground))"
          strokeWidth="8"
          fill="none"
          className="opacity-20"
        />

        {/* Progress circle */}
        <circle
          cx="128"
          cy="128"
          r={radius}
          stroke="url(#progressGradient)"
          strokeWidth="8"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          filter="url(#glow)"
          className="transition-all duration-700 ease-out"
          style={{
            transformOrigin: '50% 50%',
          }}
        />

        {/* Inner decorative circle */}
        <circle
          cx="128"
          cy="128"
          r="78"
          stroke="url(#progressGradient)"
          strokeWidth="2"
          fill="none"
          className="opacity-30 animate-pulse"
        />
      </svg>

      {/* Percentage text in the center */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          {/* Dark background circle for better contrast */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-36 h-36 bg-background/80 backdrop-blur-sm rounded-full border border-primary/20" />
          </div>
          
          {/* Text content */}
          <div className="relative z-10">
            <div className="text-4xl font-black text-foreground" style={{
              textShadow: '0 0 20px hsl(var(--background) / 0.8), 2px 2px 4px hsl(var(--background) / 0.9), 0 0 40px hsl(var(--background) / 0.5)'
            }}>
              {displayPercentage}%
            </div>
          </div>
        </div>
      </div>

      {/* Rotating sparkles around the circle */}
      <div className="absolute inset-0 animate-spin" style={{ animationDuration: '8s' }}>
        {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, index) => (
          <div
            key={angle}
            className="absolute w-2 h-2 bg-primary rounded-full opacity-60 animate-pulse"
            style={{
              left: '50%',
              top: '50%',
              transform: `
                translate(-50%, -50%) 
                rotate(${angle}deg) 
                translateY(-${radius + 25}px)
              `,
              animationDelay: `${index * 0.2}s`,
            }}
          />
        ))}
      </div>

      {/* Outer glow effect */}
      <div 
        className="absolute inset-0 rounded-full opacity-20 animate-pulse"
        style={{
          background: `conic-gradient(from 0deg, hsl(var(--primary)), hsl(var(--primary) / 0.9), hsl(var(--primary) / 0.8), hsl(var(--primary) / 0.7), hsl(var(--primary) / 0.6), hsl(var(--primary)))`,
          filter: 'blur(20px)',
          transform: 'scale(1.1)',
        }}
      />
    </div>
  );
};

export default CircularProgressSpinner;
